// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — the yard, the legal moves, and what a move costs. Pure: no DOM, no clock, no
// randomness. generate.ts builds yards on top of this and solve.ts searches it.
//
// THE MODEL
// A yard is a TREE of tracks rooted at the headshunt. Every other track hangs off a parent —
// which is the whole trick that lets one rules engine express all three yard shapes: in a plain
// stub yard every road is a child of the headshunt (a star), and in a kickback yard one road is
// a child of ANOTHER ROAD, so you cannot reach it without going through its parent first.
//
// Cars on a track are stored MOUTH-FIRST: index 0 is the end nearest the parent, i.e. the end
// the loco couples to, and the last element stands against the buffer stop. The loco is always
// at the mouth of whatever track it is on, coupled to everything standing on that track — which
// is why there are exactly two verbs:
//
//   PUSH(child)  the loco propels its whole rake into a child road and stops there.
//                cars[child] = cars[at] ++ cars[child];  cars[at] = [];  at = child
//   PULL(k)      the loco draws the k cars nearest it out onto the parent road, uncoupling
//                behind the k-th. cars[parent] = cars[at][0..k) ++ cars[parent];
//                cars[at] = cars[at][k..);  at = parent
//
// PUSH and PULL are EXACT INVERSES of one another, so the move graph is UNDIRECTED: every
// position can be walked back to the one before it, and therefore every position reachable from
// a solvable start is itself solvable. THE PLAYER CAN NEVER BRICK A YARD. That is a property of
// the model rather than a promise in the copy, and rules.test.ts pins it.
//
// The third verb exists only where the yard has a run-round loop:
//   ROUND        the loco uncouples, runs round the loop and couples to the far end of its
//                rake, so the rake now presents to the sidings back-to-front.
//                cars[root] = reverse(cars[root]).  Its own inverse; costs ROUND_COST.
//
// COST. Every push and every pull is one MOVE, because each is a change of direction — the loco
// alternates propelling into a road and drawing back out of it, and a change of direction is the
// expensive thing in a real yard. A run-round is two, because it is a there-and-back.

export const ROUND_COST = 2;

export interface TrackSpec {
  /** Display name — "Headshunt", "Road 1", "Kickback". */
  readonly name: string;
  /** Short label drawn on the board (1-3 chars). */
  readonly label: string;
  /** Index of the track this one hangs off, or null for the root (the headshunt). */
  readonly parent: number | null;
  /** How many CARS may stand here (the loco needs no room of its own). */
  readonly cap: number;
}

export interface YardSpec {
  readonly tracks: readonly TrackSpec[];
  /** The road the finished train must stand on. */
  readonly departure: number;
  /** Whether the headshunt has a run-round loop (enables the ROUND move). */
  readonly loop: boolean;
}

export interface Yard {
  readonly spec: YardSpec;
  /** cars[track][i] — mouth-first; index 0 is the end the loco couples to. */
  readonly cars: number[][];
  /** Which track the loco is standing on. */
  locoAt: number;
  /** The order the departure road must end up in, mouth-first (= front of the train first). */
  readonly target: readonly number[];
}

export type Move =
  | { readonly t: 'push'; readonly to: number }
  | { readonly t: 'pull'; readonly k: number }
  | { readonly t: 'round' };

export const ROOT = 0;

/** The children of a track, in track order. Stable, so every search is deterministic. */
export function childrenOf(spec: YardSpec, track: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < spec.tracks.length; i++) {
    if (spec.tracks[i].parent === track) out.push(i);
  }
  return out;
}

export function cloneYard(y: Yard): Yard {
  return {
    spec: y.spec,
    cars: y.cars.map((t) => t.slice()),
    locoAt: y.locoAt,
    target: y.target,
  };
}

export function moveCost(m: Move): number {
  return m.t === 'round' ? ROUND_COST : 1;
}

/**
 * Every move that may be played right now, in a stable order: pushes (by track index), then
 * pulls (by k ascending), then the run-round. Deterministic — the solver's tie-breaking and the
 * bot's choices both depend on this order being the same on every peer.
 */
export function legalMoves(y: Yard): Move[] {
  const spec = y.spec;
  const at = y.locoAt;
  const here = y.cars[at].length;
  const out: Move[] = [];

  for (const c of childrenOf(spec, at)) {
    if (here + y.cars[c].length <= spec.tracks[c].cap) out.push({ t: 'push', to: c });
  }

  const parent = spec.tracks[at].parent;
  if (parent !== null) {
    const room = spec.tracks[parent].cap - y.cars[parent].length;
    const maxK = Math.min(here, Math.max(0, room));
    for (let k = 0; k <= maxK; k++) out.push({ t: 'pull', k });
  }

  if (spec.loop && at === ROOT && here >= 2) out.push({ t: 'round' });

  return out;
}

export function isLegal(y: Yard, m: Move): boolean {
  return legalMoves(y).some((l) => sameMove(l, m));
}

export function sameMove(a: Move, b: Move): boolean {
  if (a.t !== b.t) return false;
  if (a.t === 'push' && b.t === 'push') return a.to === b.to;
  if (a.t === 'pull' && b.t === 'pull') return a.k === b.k;
  return true;
}

/** Apply a move in place. The caller must have checked it is legal. */
export function applyMove(y: Yard, m: Move): void {
  if (m.t === 'push') {
    const from = y.locoAt;
    y.cars[m.to] = y.cars[from].concat(y.cars[m.to]);
    y.cars[from] = [];
    y.locoAt = m.to;
    return;
  }
  if (m.t === 'pull') {
    const from = y.locoAt;
    const parent = y.spec.tracks[from].parent;
    if (parent === null) return;
    const taken = y.cars[from].slice(0, m.k);
    y.cars[from] = y.cars[from].slice(m.k);
    y.cars[parent] = taken.concat(y.cars[parent]);
    y.locoAt = parent;
    return;
  }
  y.cars[ROOT].reverse();
}

/** The move that undoes `m` from the state it produced. Used to prove reversibility. */
export function inverseOf(y: Yard, m: Move): Move {
  // `y` is the state BEFORE m was applied.
  if (m.t === 'push') return { t: 'pull', k: y.cars[y.locoAt].length };
  if (m.t === 'pull') return { t: 'push', to: y.locoAt };
  return { t: 'round' };
}

/** The train is made up the moment the departure road holds exactly the ordered rake. */
export function isSolved(y: Yard): boolean {
  const road = y.cars[y.spec.departure];
  if (road.length !== y.target.length) return false;
  for (let i = 0; i < road.length; i++) {
    if (road[i] !== y.target[i]) return false;
  }
  return true;
}

/**
 * How many cars of the target are already correctly seated at the mouth of the departure road.
 * Progress for the HUD only — never a rule, and never a hint about the optimal line.
 */
export function seatedCount(y: Yard): number {
  const road = y.cars[y.spec.departure];
  let n = 0;
  while (n < road.length && n < y.target.length && road[n] === y.target[n]) n++;
  return n;
}

/**
 * A compact, collision-free key for a position. Cars are letters from 'A', tracks are separated
 * by '|', and the loco's track is a digit — printable characters only, so a key can be logged or
 * diffed without turning a file into binary (see tests/source-hygiene.test.ts).
 */
export function keyOf(y: Yard): string {
  let s = String(y.locoAt);
  for (let t = 0; t < y.cars.length; t++) {
    s += '|';
    const road = y.cars[t];
    for (let i = 0; i < road.length; i++) s += String.fromCharCode(65 + road[i]);
  }
  return s;
}

/** Total cars in the yard. */
export function carCount(y: Yard): number {
  let n = 0;
  for (const t of y.cars) n += t.length;
  return n;
}

/** Every car id present, ascending. */
export function carsIn(y: Yard): number[] {
  const out: number[] = [];
  for (const t of y.cars) out.push(...t);
  return out.sort((a, b) => a - b);
}
